// ประวัติการแก้ไข Billing — turning stored rows into a readable change history.
//
// GET /api/billing/audit returns billing ROWS (including voided ones) each
// carrying a `log[]`. A person does not want rows, they want events in time
// order: "who added this, who changed the price, who deleted it". This module
// is that translation, kept out of the page so it can be reasoned about and
// tested on its own.
//
// ⚠️ THE LOG IS INCOMPLETE BY DESIGN OF HISTORY, not by accident — four writers
// have touched this table and they log differently (all verified live
// 2026-08-15):
//
//   Bubble create/edit   no entry at all, or the legacy placeholder
//                        {timestamp:null, employee_id:0, message:"", type:""}.
//                        Its POST /billing is a full-record replace that WIPES
//                        whatever log was there.
//   Bubble delete        a real entry: "แนน ศรุตยา Delete Billing."   type "delete"
//   Bubble promotion     "ราม - ราม Add Billing By Promotion [… 1/6], TOTAL = …"
//                        type "Add_promotion", employee_id 0 (name is in the text)
//   claude 563/564       "ราม ราม เพิ่มรายการ ขูดหินปูน · จำนวน 1 · รวม 1000 · DF 480"
//                        types "add" / "edit" / "delete", employee_id set
//
// So the history is REBUILT from two sources: the log where it exists, and the
// row's own audit columns where it does not. Two events are synthesised:
//
//   • a `created` event from Creation_Date + creator, when no add-type entry
//     exists — otherwise every Bubble-written line would appear out of nowhere.
//   • a `voided` event from modified_timestamp, when active === false and the
//     log has no delete entry — a void that lost its log entry to a later
//     Bubble edit must still be visible. Voids are the whole point.
//
// Synthesised events are marked `inferred: true` so the page can say so rather
// than presenting a reconstruction as a recorded fact.

import { num } from './billing.js'

/* -------------------------------------------------------------------------- */
/* event types                                                                */
/* -------------------------------------------------------------------------- */

export const EV = {
  CREATED: 'created',
  EDITED: 'edited',
  VOIDED: 'voided',
  OTHER: 'other',
}

// The `type` strings actually present in billing.log[], mapped onto the four
// above. Unknown types fall to OTHER and still render — a log entry nobody
// anticipated is exactly the kind of thing an audit trail must not swallow.
const TYPE_MAP = {
  add: EV.CREATED,
  add_promotion: EV.CREATED,
  edit: EV.EDITED,
  update: EV.EDITED,
  delete: EV.VOIDED,
}

export const EV_LABEL = {
  [EV.CREATED]: 'เพิ่มรายการ',
  [EV.EDITED]: 'แก้ไข',
  [EV.VOIDED]: 'ลบรายการ',
  [EV.OTHER]: 'อื่นๆ',
}

// Rose for a void, amber for an edit, slate for a create. A deletion is the one
// a reviewer must never miss, so it is the only alarming colour.
export const EV_TONE = {
  [EV.CREATED]: 'bg-slate-100 text-slate-600',
  [EV.EDITED]: 'bg-amber-100 text-amber-700',
  [EV.VOIDED]: 'bg-rose-100 text-rose-700',
  [EV.OTHER]: 'bg-slate-100 text-slate-500',
}

/* -------------------------------------------------------------------------- */
/* log-entry helpers                                                          */
/* -------------------------------------------------------------------------- */

// The legacy placeholder {timestamp:null, employee_id:0, message:"", type:""}
// is seeded on rows that were never really logged. It is not an event and must
// not render as one. (LabDetailDrawer filters the same shape out of lab.log[].)
export const isPlaceholder = (e) =>
  !e || (!e.timestamp && !String(e.message || '').trim() && !String(e.type || '').trim())

export const evType = (raw) => TYPE_MAP[String(raw || '').trim().toLowerCase()] || EV.OTHER

const asArr = (v) => (Array.isArray(v) ? v : [])
const ts = (v) => { const n = Number(v); return Number.isFinite(n) && n > 0 ? n : 0 }

/* -------------------------------------------------------------------------- */
/* the money on the line, for the event's context column                      */
/* -------------------------------------------------------------------------- */

// What this LINE is worth — the figure a reviewer scans for. `priceActualTotal`
// is the reconciling total (patient + claim); it is what both log formats print
// as "รวม"/"TOTAL", so the column and the message agree.
export const lineAmount = (row) => num(row?.priceActualTotal ?? row?.priceXunit ?? 0)

/* -------------------------------------------------------------------------- */
/* build the feed                                                             */
/* -------------------------------------------------------------------------- */

/**
 * rows (from GET /api/billing/audit) → events, newest first.
 *
 * Each event:
 *   { key, ts, type, message, employee_id, inferred, row }
 * `row` is the whole billing line, so the page can show the item, the amount,
 * the visit link and whether the line is currently void without a second lookup.
 */
export function buildEvents(rows) {
  const out = []

  for (const row of asArr(rows)) {
    const entries = asArr(row?.log).filter((e) => !isPlaceholder(e))
    let sawCreate = false
    let sawVoid = false

    entries.forEach((e, i) => {
      const t = evType(e.type)
      if (t === EV.CREATED) sawCreate = true
      if (t === EV.VOIDED) sawVoid = true
      out.push({
        key: `${row.id}:log:${i}`,
        // A logged entry with no timestamp still happened; fall back to the
        // row's own clock so it lands somewhere sane instead of at epoch 0.
        ts: ts(e.timestamp) || ts(row.modified_timestamp) || ts(row.Creation_Date),
        type: t,
        message: String(e.message || '').trim(),
        employee_id: Number(e.employee_id) || 0,
        inferred: false,
        row,
      })
    })

    // Every line was created by someone, even when nothing recorded it.
    if (!sawCreate && ts(row?.Creation_Date)) {
      out.push({
        key: `${row.id}:created`,
        ts: ts(row.Creation_Date),
        type: EV.CREATED,
        message: '',
        employee_id: Number(row.creator) || 0,
        inferred: true,
        row,
      })
    }

    // A void with no surviving log entry must still surface.
    if (row?.active === false && !sawVoid) {
      out.push({
        key: `${row.id}:voided`,
        ts: ts(row.modified_timestamp) || ts(row.Creation_Date),
        type: EV.VOIDED,
        message: '',
        employee_id: Number(row.modified_by) || 0,
        inferred: true,
        row,
      })
    }
  }

  // Newest first. Tie-break on the key so the order is stable across renders —
  // several events can share a millisecond (a create and its autofee sibling).
  return out.sort((a, b) => b.ts - a.ts || String(a.key).localeCompare(String(b.key)))
}

/* -------------------------------------------------------------------------- */
/* grouping + filtering                                                       */
/* -------------------------------------------------------------------------- */

const BKK_DAY = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Bangkok', year: 'numeric', month: '2-digit', day: '2-digit',
})
const BKK_TIME = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Bangkok', hour: '2-digit', minute: '2-digit',
})

export const evDay = (e) => (e.ts ? BKK_DAY.format(new Date(e.ts)) : '')
export const evTime = (e) => (e.ts ? BKK_TIME.format(new Date(e.ts)) : '—')

/** [{ day, items }] in feed order — the events are already sorted. */
export function groupByDay(events) {
  const out = []
  let cur = null
  for (const e of events) {
    const d = evDay(e)
    if (!cur || cur.day !== d) { cur = { day: d, items: [] }; out.push(cur) }
    cur.items.push(e)
  }
  return out
}

export const FILTERS = [
  { key: 'all', label: 'ทั้งหมด' },
  { key: EV.VOIDED, label: 'ลบรายการ' },
  { key: EV.EDITED, label: 'แก้ไข' },
  { key: EV.CREATED, label: 'เพิ่มรายการ' },
]

export const applyFilter = (events, key) =>
  (key === 'all' ? events : events.filter((e) => e.type === key))
